import { BackendUser, CommentPost, UserFunction } from 'common/types'
import { ITagSearchItem } from 'common/types/hashtags.types'
import { IMentioned, IMentionsState } from 'common/types/mentions.types'
import { createMentionsState } from 'common/utils/createMentionsState'
import { getHTMLTextLength } from 'common/utils/getHTMLTextLength'
import { getHashtagsFromHTML } from 'common/utils/getHashtagsFromHTML'
import { getMentionsTags } from 'common/utils/getMentionsTags'
import { removeHashtagLinks } from 'common/utils/removeHashtagLinks'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useFormatHours } from 'hooks/use-format-hours/useFormatHours'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useEffect, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'

const initialCommentState = createMentionsState('comment-type')

interface IUseManageComments {
  deleteComment: Function
  comments: CommentPost[]
  setComments: Function
  setCommentCount: Function
  user: BackendUser
  post: any
  comment: CommentPost
  updateComment: Function
  backendUserFunctions: UserFunction[]
  mentions: Map<string, IMentioned>
  tags: Map<string, ITagSearchItem>
}

export const useManageComments = ({
  deleteComment,
  comments,
  setComments,
  setCommentCount,
  user,
  post,
  comment,
  updateComment,
  backendUserFunctions,
  mentions,
  tags
}: IUseManageComments) => {
  const [showEdit, setShowEdit] = useState(false)
  const { backendPartner } = useAuth()
  const { logEventAnalytics } = useAnalytics()
  const history = useHistory()

  const [editedCommentMentionsState, editedCommentMentionStateDispatch] = useReducer<
    (state: IMentionsState, action: Partial<IMentionsState>) => IMentionsState
  >((state, action) => {
    return { ...state, ...action }
  }, initialCommentState)

  const deleteCommentPermission = backendUserFunctions?.find(x => x?.name === 'Delete Comment')
  const editCommentPermission = backendUserFunctions?.find(x => x?.name === 'Edit Comment')
  const viewLogPermission = backendUserFunctions?.find(x => x?.name === 'Activity Log')
  const maxCommentLength = 250
  const notPassingMaxLength = editedCommentMentionsState.textfieldCharacterLength <= maxCommentLength
  const notEmpty = editedCommentMentionsState.textfieldCharacterLength > 0

  const removeComment = async (userId: string, commentContentId: string, commentId: string) => {
    await deleteComment(userId, commentContentId, commentId)
    const newComments = comments.filter(comment => comment?.commentId !== commentId)
    setComments(newComments)
    setCommentCount(prevCommentCount => prevCommentCount - 1)
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = user?.id

    if (user?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }
    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'delete comment',
      'mixpanel',
      'delete comment clicked',
      { 'Post ID': post?.id, ...partnerLog, ...userLog },
      user?.id
    )
  }

  const _tags = tags

  const editComment = async (
    userId: string,
    commentId: string,
    comments: CommentPost[] | any,
    updatedComment: string,
    partnerId?: string
  ) => {
    const { mentioned, tagged: tags, html } = getMentionsTags({
      mentions,
      mentionedClassIdentifier: ['.edit-comment-val-mentioned'],
      tags: _tags,
      html: updatedComment,
      inputElement: document.querySelector(`.${editedCommentMentionsState?.textfieldId}`)
    })

    const formattedUpdatedComment = html.replaceAll('edit-comment-val-mentioned', 'comment-val-mentioned')
    
    const getCommentIndex = () => {
      if (comment.partnerId) {
        return comments?.findIndex(comment => comment.commentId === commentId && partnerId === comment.partnerId)
      } else {
        return comments?.findIndex(comment => comment.commentId === commentId && userId === comment.userId)
      }
    }
    const commentIndex = getCommentIndex()
    const arrayWithUpdatedComment = [...comments]

    arrayWithUpdatedComment[commentIndex]!.content[0]!.comment = formattedUpdatedComment
    setComments(arrayWithUpdatedComment)
    await updateComment(userId, commentId, formattedUpdatedComment, tags, mentioned)
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = user?.id

    if (user?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }
    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'comment edit',
      'mixpanel',
      'comment edit clicked',
      {
        'Post ID': post?.id,
        'Time after comment': useFormatHours(
          (new Date(comment?.updatedAt).getTime() - new Date(comment?.createdAt).getTime()) / (1000 * 3600)
        ),
        ...partnerLog,
        ...userLog
      },
      user?.id
    )
  }

  const handleEditComment = () => {
    const html = comment?.content[0]?.comment?.replaceAll('comment-val-mentioned', 'edit-comment-val-mentioned')
    const content = removeHashtagLinks(html)

    editedCommentMentionStateDispatch({
      ...initialCommentState,
      textfieldValue: content
    })
    setShowEdit(true)
  }

  const handleClick = () => {
    if (!editedCommentMentionsState?.textfieldValue.trim()?.length) return
    editComment(user?.id, comment?.commentId, comments, editedCommentMentionsState?.textfieldValue, user?.partnerId)
    setShowEdit(false)
  }

  const handleClose = () => {
    setShowEdit(false)
  }

  const getName = (): string => {
    if (comment?.companyName) {
      return comment?.companyName
    } else {
      return `${comment?.firstName} ${comment?.lastName}`
    }
  }

  const commentCreatorLink = () => {
    if (comment?.partnerId) {
      history.push(`/company/${comment?.partnerId}`)
    } else {
      user?.id === comment?.userId ? history.push(`/profile/`) : history.push(`/profile/${comment?.userId}`)
    }
  }

  const renderOption = (option: string) => {
    if (!comment?.partnerId) return true
    switch (option) {
      case 'delete': {
        return deleteCommentPermission ? true : false
      }
      case 'edit': {
        return editCommentPermission ? true : false
      }
      case 'log': {
        return viewLogPermission ? true : false
      }
    }
  }

  const checkForPartnerComment = () => {
    if (!comment?.partnerId) return false
    if (comment?.partnerId && user?.partnerId) {
      if (comment?.partnerId === user?.partnerId && (editCommentPermission || deleteCommentPermission)) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  useEffect(() => {
    const characterLength = getHTMLTextLength(editedCommentMentionsState?.textfieldValue)

    editedCommentMentionStateDispatch({ textfieldCharacterLength: characterLength })
  }, [editedCommentMentionsState.textfieldValue])

  return {
    removeComment,
    editComment,
    handleEditComment,
    handleClick,
    handleClose,
    getName,
    commentCreatorLink,
    renderOption,
    checkForPartnerComment,
    showEdit,
    setShowEdit,
    notPassingMaxLength,
    notEmpty,
    editCommentPermission,
    deleteCommentPermission,
    maxCommentLength,
    editedCommentMentionsState,
    editedCommentMentionStateDispatch
  } as const
}
